/* eslint-disable react/display-name */

import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { GridColDef } from "@material-ui/data-grid";

import BaseDataGrid from "../../../../../components/modules/BaseDataGrid";
import ConfirmationModal from "../../../../../components/modules/ConfirmationModal";
import LineItemsReportDashboardToolbar from "./LineItemsReportDashboardToolbar";
import Loader from "../../../../../components/elements/Loader";
import Switch from "../../../../../components/elements/Switch";
import api from "../../../../../api";
import { BrandgenCampaignPool, ReportTimeFrames } from "../../../../../types/campaignTypes";
import { RowType, transformRowValue } from "../../../../../helpers/campainRowFormatHelpers";
import { adLibFloatingNumbersFields, adLibMetricsColumns } from "../../constants";
import { useCampaignDashboardStyles } from "../../CampaignDashboardStylesContext";

export type AdLibCampaignReportItem = {
  id?: string;
  adLibCampaignId?: number;
  brandgenCampaignId?: string;
  timeFrame: string;
  adLibAdvertiserId?: number;
  campaignName?: string | null;
  currencyCode?: string | null;
  date?: string | null;
  impressions?: number | null;
  clicks?: number | null;
  videoViews?: number | null;
  videoComplete?: number | null;
  ctr?: number | null;
  cpm?: number | null;
  cpc?: number | null;
  cpv?: number | null;
  cpcv?: number | null;
  cpa?: number | null;
  totalConversions?: number | null;
  totalRevenue?: number | null;
  totalSpend?: number | null;
  nonBilledDataCost?: number | null;
  billedDataCost?: number | null;
  createdAt?: string;
  updatedAt?: string;
};

export type AdLibCampaignWithReports = {
  brandgenCampaignId: string;
  campaignPoolId?: string;
  adLibCampaignId?: number;
  adLibAdvertiserId?: number;
  campaignName?: string;
  campaignBudget?: number;
  active: boolean;
  hubspotCompanyId?: string | null;
  hubspotLineItemId?: string | null;
  fromFile?: string | null;
  adLibCampaignReports: AdLibCampaignReportItem[];
  createdAt?: string;
  updatedAt?: string;
};

const fetchLineItemsWithReport = async (brandgenCampaignPoolId: string) => {
  try {
    const { payload } = await api.getLineItemReports(brandgenCampaignPoolId);
    return payload;
  } catch (error) {
    const err = error as AxiosError;
    throw new Error(err.message);
  }
};

const fetchLineItemsDailyReport = async (brandgenCampaignId: string, date: Date) => {
  try {
    const { payload } = await api.getLineItemDailyReport(brandgenCampaignId, date);
    return payload;
  } catch (error) {
    const err = error as AxiosError;
    throw new Error(err.message);
  }
};

type CampaignLineItemsReportDashboardProps = {
  selectedTimeFrame: ReportTimeFrames;
  campaign: BrandgenCampaignPool;
  customReportDate: Date | undefined;
  isCustomDateChanged: boolean;
};

type LineItemToUpdate = {
  id: number;
  active: boolean;
  isAdLib?: boolean;
};

const CampaignLineItemsReportDashboard: FC<CampaignLineItemsReportDashboardProps> = ({
  selectedTimeFrame,
  campaign,
  customReportDate,
  isCustomDateChanged,
}) => {
  const classes = useCampaignDashboardStyles();
  const [lineItems, setLineItems] = useState<AdLibCampaignWithReports[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sourceHeaderName, setSourceHeaderName] = useState<string>("Source");

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [lineItemToUpdate, setLineItemToUpdate] = useState<LineItemToUpdate | null>(null);

  const handleOpenConfirmationModal = () => setIsConfirmationModalOpen(true);
  const handleCloseConfirmationModal = () => setIsConfirmationModalOpen(false);

  const isCustomDate = selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE;

  useEffect(() => {
    if (isCustomDate) {
      return undefined;
    }

    let cancelled = false;
    const fetchBase = async () => {
      try {
        setIsLoading(true);
        const response = await fetchLineItemsWithReport(campaign.brandgenCampaignPoolId);
        if (cancelled) return;
        setLineItems(Array.isArray(response) ? response : []);
      } catch (error) {
        if (!cancelled) console.error("fetchAndSetLineItemReports error: ", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchBase();
    return () => {
      cancelled = true;
    };
  }, [campaign.brandgenCampaignPoolId, campaign.active, isCustomDate]);

  useEffect(() => {
    if (!isCustomDate) {
      return undefined;
    }
    if (!customReportDate || !isCustomDateChanged) {
      return undefined;
    }

    let cancelled = false;
    const fetchDaily = async () => {
      try {
        setIsLoading(true);
        const response = await fetchLineItemsDailyReport(campaign.brandgenCampaignPoolId, customReportDate);
        if (cancelled) return;
        if (response && typeof response === "object" && !Array.isArray(response) && "lineItems" in response) {
          const res = response as { lineItems?: AdLibCampaignWithReports[] };
          setLineItems(res.lineItems ?? []);
        } else {
          setLineItems(Array.isArray(response) ? response : []);
        }
      } catch (error) {
        if (!cancelled) console.error("fetchAndSetLineItemReports error: ", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchDaily();
    return () => {
      cancelled = true;
    };
  }, [campaign.brandgenCampaignPoolId, isCustomDate, customReportDate, isCustomDateChanged]);

  useEffect(() => {
    if (lineItems && lineItems.length > 0) {
      const first = lineItems[0];
      const hubspotCompanyId = "hubspotCompanyId" in first ? first.hubspotCompanyId : null;
      setSourceHeaderName(!hubspotCompanyId ? "File Upload" : "Source");
    } else {
      setSourceHeaderName("Source");
    }
  }, [lineItems]);

  const updateLineItemActiveStatus = async () => {
    try {
      if (!lineItemToUpdate) return;

      setIsLoading(true);
      await api.updateLineItemStatus(lineItemToUpdate.id, !lineItemToUpdate.active);
      const fetchedLineItemReports = await fetchLineItemsWithReport(campaign.brandgenCampaignPoolId);
      setLineItems(Array.isArray(fetchedLineItemReports) ? fetchedLineItemReports : []);
      setIsLoading(false);
      setLineItemToUpdate(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("updateLineItemActiveStatus error: ", error);
      setIsLoading(false);
    }
  };

  const handleLineItemActiveStatusChange = useCallback(
    (id: number, active: boolean) => () => {
      handleOpenConfirmationModal();
      setLineItemToUpdate({ id, active });
    },
    [],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "active",
        headerName: "Status",
        width: 150,
        renderCell: (params: any) => (
          <React.Fragment key={params.row.id}>
            <Typography className={params.row.active === "ACTIVE" ? classes.activeStatus : classes.inactiveStatus}>
              {params.row.active}
            </Typography>
            <Switch
              checked={params.row.active === "ACTIVE"}
              onChange={handleLineItemActiveStatusChange(params.row.rowIdForStatus, params.row.active === "ACTIVE")}
            />
          </React.Fragment>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        width: 400,
        renderCell: ({ value }) => (
          <Tooltip title={value as string}>
            <Typography>{value as string}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "source",
        headerName: sourceHeaderName,
        width: 200,
      },
      {
        field: "hsCompanyId",
        headerName: "HS Company Id",
        width: 180,
      },
      ...adLibMetricsColumns.map(({ hide, ...col }) => col),
    ],
    [handleLineItemActiveStatusChange, sourceHeaderName, classes],
  );

  const rows = useMemo(() => {
    return lineItems.map((item, index) => {
      const { adLibCampaignId, campaignName, active, hubspotCompanyId, fromFile, adLibCampaignReports } = item;

      const report = adLibCampaignReports?.find((r) => r.timeFrame === selectedTimeFrame);

      const rowId = String(adLibCampaignId);
      const baseRow: { [key: string]: any } = {
        id: rowId + index,
        rowIdForStatus: adLibCampaignId,
        hsCompanyId: hubspotCompanyId ?? "N/A",
        source: hubspotCompanyId ? "hs" : fromFile ?? "file",
        name: campaignName ?? "N/A",
        active: active ? "ACTIVE" : "INACTIVE",
      };

      if (report) {
        for (const metric of adLibMetricsColumns) {
          const value = report[metric.field as keyof AdLibCampaignReportItem];
          const isFloat = adLibFloatingNumbersFields.includes(metric.field);
          baseRow[metric.field] =
            value !== undefined && value !== null
              ? transformRowValue(Number(value), metric.type === "FLAT", isFloat, metric.type as RowType)
              : "N/A";
        }
      } else {
        adLibMetricsColumns.forEach((metric) => {
          baseRow[metric.field] = "N/A";
        });
      }
      return baseRow;
    });
  }, [lineItems, selectedTimeFrame]);

  const isMaxRowsAmountExceeded = rows.length > 10;

  return (
    <div
      className={`${classes.lineItemsDashboard} ${
        isMaxRowsAmountExceeded ? classes.lineItemsDashboardFixedHeight : ""
      }`}
    >
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        handleClose={handleCloseConfirmationModal}
        confirmQuestion="Are you sure you want to update this line item's status?"
        confirmButtonLabel="Yes"
        handleConfirm={updateLineItemActiveStatus}
      />
      {isLoading ? (
        <Loader className={classes.loader} isAbsolutePosition={false} size={42} />
      ) : (
        <>
          <BaseDataGrid
            components={{
              Toolbar: LineItemsReportDashboardToolbar,
            }}
            localeText={{ toolbarColumns: "Select Metrics" }}
            autoHeight={!isMaxRowsAmountExceeded}
            columns={columns}
            rows={rows.some((row) => Object.keys(row).length > 0) ? rows : []}
          />
        </>
      )}
    </div>
  );
};

export default CampaignLineItemsReportDashboard;
